import { useState, useCallback } from 'react';
import { supabaseClient } from '../config/supabase';

export interface SurveyHealth {
  id: string;
  title: string;
  status: string;
  target_programs: string[];
  rating_questions: number;
  peo_references: number;
  broken_references: number;
  health_status: 'HEALTHY' | 'WARNING' | 'CRITICAL';
}

export interface BrokenReference {
  question_id: string;
  missing_peo_id: string;
  peo_title: string;
  status: 'MISSING' | 'ARCHIVED';
  message: string;
}

export interface DeploymentCheck {
  can_deploy: boolean;
  broken_count: number;
  error_message: string;
}

export interface IntegrityReport {
  survey_title: string;
  survey_status: string;
  total_questions: number;
  rating_questions: number;
  peo_references: number;
  broken_references: number;
  health_status: string;
  broken_details: BrokenReference[];
}

export function useSurveyProtection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get all surveys health status
  const getSurveysHealth = useCallback(async (): Promise<SurveyHealth[]> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient
        .from('survey_health_status')
        .select('*')
        .order('health_status');

      if (error) throw error;
      return data || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch survey health';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if survey can be deployed
  const canDeploySurvey = useCallback(async (surveyId: string): Promise<DeploymentCheck> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient.rpc('can_deploy_survey', {
        survey_id: surveyId,
      });

      if (error) throw error;
      return data[0];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check deployment status';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get detailed integrity report
  const getIntegrityReport = useCallback(async (surveyId: string): Promise<IntegrityReport> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient.rpc('get_survey_integrity_report', {
        survey_id: surveyId,
      });

      if (error) throw error;
      return data[0];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch integrity report';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate survey PEO references
  const validateSurveyReferences = useCallback(
    async (surveyId: string): Promise<BrokenReference[]> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabaseClient.rpc('validate_survey_peo_references', {
          survey_id: surveyId,
        });

        if (error) throw error;
        return data || [];
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to validate references';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Soft delete a PEO/PO
  const softDeletePEO = useCallback(async (peoId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient.rpc('soft_delete_peo', {
        peo_id: peoId,
      });

      if (error) throw error;
      return data[0];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to soft delete PEO';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Restore a soft-deleted PEO/PO
  const restorePEO = useCallback(async (peoId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient.rpc('restore_peo', {
        peo_id: peoId,
      });

      if (error) throw error;
      return data[0];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to restore PEO';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Find all surveys with broken references
  const findBrokenSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient.rpc('find_surveys_with_broken_references');

      if (error) throw error;
      return data || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to find broken surveys';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getSurveysHealth,
    canDeploySurvey,
    getIntegrityReport,
    validateSurveyReferences,
    softDeletePEO,
    restorePEO,
    findBrokenSurveys,
  };
}
